import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Users, TrendingUp, Download, Trash2, Eye, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'
  const [selectedLeads, setSelectedLeads] = useState(new Set()); // For bulk operations
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch active leads
      const leadsResponse = await fetch(`${backendUrl}/api/admin/leads`, {
        credentials: 'include'
      });
      
      if (leadsResponse.ok) {
        const leadsData = await leadsResponse.json();
        setLeads(leadsData);
      }

      // Fetch archived leads
      const archivedResponse = await fetch(`${backendUrl}/api/admin/archived`, {
        credentials: 'include'
      });
      
      if (archivedResponse.ok) {
        const archivedData = await archivedResponse.json();
        setArchivedLeads(archivedData);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Daten",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCSVExport = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/leads/csv`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `leads_all_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Export erfolgreich",
          description: "CSV-Datei wurde heruntergeladen",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('CSV Export Error:', error);
      toast({
        title: "Export fehlgeschlagen",
        description: "Fehler beim CSV-Export",
        variant: "destructive"
      });
    }
  };

  // Archive function removed

  const handleDeleteLead = async (leadEmail) => {
    if (!confirm('Lead wirklich permanent löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/archived/${encodeURIComponent(leadEmail)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchData();
        toast({
          title: "Lead gelöscht",
          description: "Lead wurde permanent gelöscht",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Delete Error:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen des Leads",
        variant: "destructive"
      });
    }
  };

  // Bulk operations
  const handleSelectLead = (leadEmail) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadEmail)) {
      newSelected.delete(leadEmail);
    } else {
      newSelected.add(leadEmail);
    }
    setSelectedLeads(newSelected);
  };

  const handleSelectAll = () => {
    const currentEmails = currentLeads.map(lead => lead.email);
    if (currentTabSelectedLeads.size === currentEmails.length && currentEmails.length > 0) {
      // Remove all current tab leads from selection
      const newSelected = new Set(selectedLeads);
      currentEmails.forEach(email => newSelected.delete(email));
      setSelectedLeads(newSelected);
    } else {
      // Add all current tab leads to selection
      const newSelected = new Set(selectedLeads);
      currentEmails.forEach(email => newSelected.add(email));
      setSelectedLeads(newSelected);
    }
  };

  // Bulk archive function removed

  const handleBulkCSV = async () => {
    const currentSelected = Array.from(currentTabSelectedLeads);
    if (currentSelected.length === 0) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/leads/bulk-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emails: currentSelected })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `selected_leads_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "CSV Export",
          description: `${currentSelected.length} ausgewählte Leads exportiert`,
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Bulk CSV Error:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim CSV-Export",
        variant: "destructive"
      });
    }
  };

  const handleSingleCSV = async (leadEmail) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/leads/${encodeURIComponent(leadEmail)}/csv`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lead_${leadEmail.replace('@', '_').replace('.', '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "CSV Export",
          description: `Lead ${leadEmail} erfolgreich exportiert`,
          variant: "default"
        });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Single CSV Error:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim CSV-Export des Leads",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    window.location.href = '/';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  const currentLeads = activeTab === 'active' ? leads : archivedLeads;
  
  // Filter selected leads to only include those from current tab
  const currentTabSelectedLeads = new Set(
    Array.from(selectedLeads).filter(email => 
      currentLeads.some(lead => lead.email === email)
    )
  );

  // Debug logging for bulk selection
  console.log('Debug - Archive Tab Selection:', {
    activeTab,
    currentLeadsCount: currentLeads.length,
    globalSelectedCount: selectedLeads.size,
    currentTabSelectedCount: currentTabSelectedLeads.size,
    selectedLeadsArray: Array.from(selectedLeads),
    currentTabSelectedArray: Array.from(currentTabSelectedLeads)
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Wärmepumpen Lead-Verwaltung (Alle Leads)</p>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="hover:bg-red-50 hover:border-red-300"
            >
              Abmelden
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Active/Archived Tabs */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'active' 
                  ? 'bg-white text-brand-green shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Aktive Leads ({leads.length})
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'archived' 
                  ? 'bg-white text-brand-green shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Archivierte Leads ({archivedLeads.length})
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {currentTabSelectedLeads.size > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-blue-800 font-medium">
                  {currentTabSelectedLeads.size} Lead{currentTabSelectedLeads.size > 1 ? 's' : ''} ausgewählt
                </span>
                <button
                  onClick={() => setSelectedLeads(new Set())}
                  className="text-blue-600 hover:text-blue-800 underline text-sm"
                >
                  Auswahl aufheben
                </button>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkCSV}
                  className="text-blue-700 border-blue-300 hover:bg-blue-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV Export ({currentTabSelectedLeads.size})
                </Button>
                {/* Bulk archive functionality removed */}
              </div>
            </div>
          </div>
        )}

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                {activeTab === 'active' ? 'Aktive Leads' : 'Archivierte Leads'} ({currentLeads.length})
              </CardTitle>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={handleCSVExport}>
                  <Download className="w-4 h-4 mr-2" />
                  CSV Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {currentLeads.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">
                  {activeTab === 'active' ? 'Keine aktiven Leads verfügbar' : 'Keine archivierten Leads verfügbar'}
                </p>
              </div>
            ) : (
              <div className="relative">
                <div className="overflow-x-auto" style={{ overflowX: 'scroll' }}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left sticky left-0 bg-gray-50 z-10">
                          <input
                            type="checkbox"
                            checked={currentLeads.length > 0 && currentTabSelectedLeads.size === currentLeads.length}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-brand-green focus:ring-brand-green"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-16 bg-gray-50 z-10">Aktionen</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-Mail</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefon</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentLeads.map((lead, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                            <input
                              type="checkbox"
                              checked={selectedLeads.has(lead.email)}
                              onChange={() => handleSelectLead(lead.email)}
                              className="rounded border-gray-300 text-brand-green focus:ring-brand-green"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap sticky left-16 bg-white z-10">
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setSelectedLead(lead)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Details
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleSingleCSV(lead.email)}
                                className="text-blue-600 hover:text-blue-800 hover:border-blue-300"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                CSV
                              </Button>
                              {activeTab === 'archived' && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDeleteLead(lead.email)}
                                  className="text-red-600 hover:text-red-800 hover:border-red-300"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Löschen
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{lead.firstName} {lead.lastName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">{lead.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">{lead.phone}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              (lead.qualificationScore || 0) >= 80 ? 'bg-green-100 text-green-800' :
                              (lead.qualificationScore || 0) >= 50 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {lead.qualificationScore || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                            {new Date(lead.timestamp).toLocaleDateString('de-DE')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Lead Details</h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedLead(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-6">
                {/* Schritt 1: Grunddaten + Kontakt */}
                <div className="border-b pb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Schritt 1: Grunddaten + Kontakt</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Immobilientyp</label>
                      <p className="text-gray-900">{selectedLead.propertyType || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{selectedLead.firstName} {selectedLead.lastName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">E-Mail</label>
                      <p className="text-gray-900">{selectedLead.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Telefon</label>
                      <p className="text-gray-900">{selectedLead.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Datenschutz akzeptiert</label>
                      <p className="text-gray-900">{selectedLead.acceptPrivacy ? 'Ja' : 'Nein'}</p>
                    </div>
                  </div>
                </div>

                {/* Schritt 2: Immobiliendetails */}
                <div className="border-b pb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Schritt 2: Immobiliendetails</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Baujahr</label>
                      <p className="text-gray-900">{selectedLead.buildingYear || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Wohnfläche</label>
                      <p className="text-gray-900">{selectedLead.livingSpace ? `${selectedLead.livingSpace} m²` : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Schritt 3: Aktuelle Heizung */}
                <div className="border-b pb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Schritt 3: Aktuelle Heizung</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Aktuelles Heizsystem</label>
                      <p className="text-gray-900">{selectedLead.currentHeating || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Alter der Heizung</label>
                      <p className="text-gray-900">{selectedLead.heatingAge || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Jährliche Heizkosten</label>
                      <p className="text-gray-900">{selectedLead.annualCosts || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Lead-Typ & Tracking Info */}
                <div className="border-b pb-4 bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-md font-semibold text-blue-800 mb-3">Lead-Information & Tracking</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-blue-600">Lead-Typ</label>
                      <p className="text-blue-900 font-semibold">
                        {selectedLead.leadType === 'beratung' && '🎯 Standard Beratung'}
                        {selectedLead.leadType === 'aktion' && '🔥 Aktions-Angebot'}
                        {selectedLead.leadType === 'komplettloesung' && '🌟 Komplettlösung'}
                        {!selectedLead.leadType && 'Standard Beratung'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-600">Button-Quelle</label>
                      <p className="text-blue-900">
                        {selectedLead.buttonSource === 'MonthlyActionSection' && 'Monatliche Aktion'}
                        {selectedLead.buttonSource === 'CompleteEnergySection' && 'Komplettlösung Sektion'}
                        {selectedLead.buttonSource === 'HeroSection' && 'Hero Bereich'}
                        {!selectedLead.buttonSource && 'Standard Button'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-600">Qualification Score</label>
                      <p className="text-blue-900 font-bold">{selectedLead.qualificationScore || 0} Punkte</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-blue-600">Eingegangen am</label>
                      <p className="text-blue-900">{selectedLead.timestamp ? new Date(selectedLead.timestamp).toLocaleDateString('de-DE') : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Schritt 4: Projektziel & Zusatzinfos */}
                <div className="border-b pb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Schritt 4: Projektziel & Zusatzinfos</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Projektziel</label>
                      <p className="text-gray-900">{selectedLead.projectGoal || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Zeitrahmen</label>
                      <p className="text-gray-900">{selectedLead.timeline || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Budget</label>
                      <p className="text-gray-900">{selectedLead.budget || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Newsletter gewünscht</label>
                      <p className="text-gray-900">{selectedLead.wantsNewsletter ? 'Ja' : 'Nein'}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-gray-500">Adresse</label>
                      <p className="text-gray-900">{selectedLead.address || 'N/A'}</p>
                    </div>
                    {selectedLead.message && (
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Nachricht</label>
                        <p className="text-gray-900">{selectedLead.message}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta-Informationen */}
                <div>
                  <h4 className="text-md font-semibold text-gray-800 mb-3">Lead-Informationen</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Qualifikations-Score</label>
                      <p className="text-gray-900">{selectedLead.qualificationScore || 0} Punkte</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Erstellt am</label>
                      <p className="text-gray-900">{new Date(selectedLead.timestamp).toLocaleString('de-DE')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;